import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { apiClient } from "@/lib/api";
import SEO from "@/components/SEO";
import { getSEO } from "@/lib/seo-config";
import { MapPin, ChevronRight, Building2, CheckCircle2, Clock } from "lucide-react";

export default function CityDirectory() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get("/public/cities")
      .then(({ data }) => setCities(data.cities || []))
      .finally(() => setLoading(false));
  }, []);

  const { activeCities, prospectedCities, totalArtisans } = useMemo(() => {
    const active = cities.filter((c) => !c.prospected);
    const prospected = cities.filter((c) => c.prospected);
    const total = active.reduce((sum, c) => sum + (c.artisans_count || 0), 0);
    return { activeCities: active, prospectedCities: prospected, totalArtisans: total };
  }, [cities]);

  return (
    <div className="min-h-screen bg-[#F7F7F9] flex flex-col" data-testid="city-directory-page">
      <SEO cfg={getSEO("/artisans/villes")} />
      <Navbar />

      <div className="border-b-2 border-[#0A0A0B] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-3 mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <Link to="/" className="hover:text-[#FF4D00]">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/artisans" className="hover:text-[#FF4D00]">Artisans</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#0A0A0B]">Par ville</span>
        </div>
      </div>

      <section className="bg-[#0A0A0B] text-white py-16 border-b-2 border-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" /> Annuaire géolocalisé
          </div>
          <h1 className="display text-4xl lg:text-6xl tracking-tighter mt-3 leading-[0.95]">
            Trouver un artisan BTP <span className="text-[#FF4D00]">par ville</span> · France
          </h1>
          <p className="mt-4 max-w-2xl text-zinc-300">
            Annuaire géolocalisé des artisans du bâtiment vérifiés SIRET Sirene/INSEE : Paris, Lyon, Marseille, Toulouse, Bordeaux, Nantes, Lille, Rennes, Strasbourg, Montpellier, Nice, Grenoble et 100+ villes. Identité contrôlée, paiement sécurisé par séquestre Stripe, certificats de fin de travaux opposables.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12 flex-1 w-full">
        {/* Comparateur banner : chiffres clés temps réel */}
        {!loading && cities.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-2 border-[#0A0A0B] mb-10 shadow-[6px_6px_0_#FF4D00]" data-testid="cities-stats-banner">
            <div className="p-6 border-r-2 border-[#0A0A0B] bg-white">
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Villes actives</div>
              <div className="display text-4xl mt-2" data-testid="stat-active-count">{activeCities.length}</div>
              <div className="text-xs text-zinc-600 mt-1">Avec artisans SIRET vérifiés</div>
            </div>
            <div className="p-6 md:border-r-2 border-[#0A0A0B] bg-white">
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Artisans total</div>
              <div className="display text-4xl mt-2" data-testid="stat-total-artisans">{totalArtisans}</div>
              <div className="text-xs text-zinc-600 mt-1">Sur toutes les villes actives</div>
            </div>
            <div className="p-6 border-t-2 md:border-t-0 border-r-2 border-[#0A0A0B] bg-white">
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">// Villes prospectées</div>
              <div className="display text-4xl mt-2" data-testid="stat-prospected-count">{prospectedCities.length}</div>
              <div className="text-xs text-zinc-600 mt-1">Bientôt disponibles</div>
            </div>
            <div className="p-6 border-t-2 md:border-t-0 border-[#0A0A0B] bg-[#0A0A0B] text-white">
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Total</div>
              <div className="display text-4xl mt-2">{cities.length}</div>
              <div className="text-xs text-zinc-300 mt-1">Villes référencées France</div>
            </div>
          </div>
        )}

        <h2 className="display text-3xl lg:text-4xl tracking-tighter mb-8">
          Villes où trouver un artisan Batisur
        </h2>
        {loading && <div className="mono text-sm text-zinc-500">Chargement…</div>}
        {!loading && cities.length === 0 && (
          <div className="border-2 border-dashed border-zinc-300 p-12 text-center">
            <Building2 className="w-10 h-10 text-zinc-400 mx-auto" />
            <div className="display text-2xl mt-4">Aucune ville référencée pour le moment</div>
            <p className="text-zinc-600 mt-2">L'annuaire grandit chaque semaine. Revenez bientôt !</p>
          </div>
        )}

        {/* Villes actives */}
        {!loading && activeCities.length > 0 && (
          <>
            <div className="flex items-center gap-3 mb-5">
              <CheckCircle2 className="w-5 h-5 text-[#FF4D00]" strokeWidth={2.5} />
              <div className="mono text-[11px] uppercase tracking-widest text-[#0A0A0B] font-semibold">
                // Villes actives · {activeCities.length}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-14" data-testid="cities-grid-active">
              {activeCities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/artisans/ville/${c.slug}`}
                  className="border-2 border-[#0A0A0B] bg-white p-6 hover:bg-[#FF4D00] hover:text-white transition group flex items-center justify-between shadow-[3px_3px_0_#0A0A0B]"
                  data-testid={`city-${c.slug}`}
                >
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-white/80">// {c.slug}</div>
                    <div className="display text-2xl tracking-tighter mt-1">{c.label}</div>
                    <div className="text-xs mt-2 opacity-80 flex items-center gap-1.5">
                      <span className="inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500 group-hover:bg-white" />
                      {c.artisans_count} artisan{c.artisans_count > 1 ? "s" : ""} vérifié{c.artisans_count > 1 ? "s" : ""}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </>
        )}

        {/* Villes prospectées (bientôt) */}
        {!loading && prospectedCities.length > 0 && (
          <>
            <div className="flex items-center gap-3 mb-5">
              <Clock className="w-5 h-5 text-zinc-500" strokeWidth={2.5} />
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-600 font-semibold">
                // Villes prospectées · Bientôt disponibles · {prospectedCities.length}
              </div>
            </div>
            <div className="border-2 border-dashed border-zinc-400 bg-white/60 p-4 mb-6">
              <p className="text-sm text-zinc-700">
                Ces villes sont en cours de recrutement d'artisans. <Link to="/pro/rejoindre" className="text-[#FF4D00] underline font-semibold">Vous êtes artisan local ?</Link> Inscrivez-vous gratuitement et devenez référent de votre ville.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="cities-grid-prospected">
              {prospectedCities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/artisans/ville/${c.slug}`}
                  className="border-2 border-zinc-400 bg-zinc-50 p-6 hover:border-[#0A0A0B] hover:bg-white transition group flex items-center justify-between"
                  data-testid={`city-prospected-${c.slug}`}
                >
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-400">// {c.slug}</div>
                    <div className="display text-2xl tracking-tighter mt-1 text-zinc-700">{c.label}</div>
                    <div className="text-xs mt-2 text-zinc-500 flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      Bientôt · artisans recherchés
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 flex-shrink-0 text-zinc-400" />
                </Link>
              ))}
            </div>
          </>
        )}
      </section>

      {/* SEO bloc — métiers couverts */}
      <section className="bg-white border-t-2 border-[#0A0A0B]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Tous les métiers du bâtiment</div>
          <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-3">
            Quels artisans trouverez-vous près de chez vous ?
          </h2>
          <p className="mt-4 max-w-3xl text-zinc-700">
            Batisur référence tous les corps de métier du BTP en France. Chaque professionnel est identifié
            légalement, son SIRET vérifié via l'INSEE et sa réputation notée par les demandeurs précédents.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { h3: "Plomberie et sanitaire", body: "Plombiers pour installation salle de bain, cuisine équipée, chauffe-eau, adoucisseur, dépannage fuite." },
              { h3: "Électricité et domotique", body: "Électriciens certifiés Qualifelec pour mise aux normes, tableau électrique, prises, éclairage LED, domotique connectée." },
              { h3: "Maçonnerie et gros œuvre", body: "Maçons pour construction, extension, ouverture de mur porteur, ravalement de façade, dallage béton." },
              { h3: "Menuiserie et fenêtres", body: "Menuisiers pour portes intérieures, portes-fenêtres double vitrage, escaliers sur mesure, dressing, cuisine équipée." },
              { h3: "Peinture et décoration", body: "Peintres en bâtiment pour murs intérieurs, façades extérieures, laque, papier peint, enduits décoratifs." },
              { h3: "Carrelage et sols", body: "Carreleurs pour salle de bain, cuisine, sols grand format, mosaïque, marbre, ainsi que parquet, stratifié, linoléum." },
              { h3: "Chauffage et climatisation", body: "Chauffagistes RGE pour installation pompe à chaleur, chaudière condensation, climatisation réversible, plancher chauffant." },
              { h3: "Toiture et couverture", body: "Couvreurs zinc, ardoise, tuile, étanchéité toit terrasse, isolation combles, ravalement zinguerie." },
              { h3: "Isolation thermique", body: "Isolation extérieure ITE, combles perdus, murs, fenêtres double vitrage — éligibles aides MaPrimeRénov' 2026." },
            ].map(({ h3, body }, i) => (
              <div key={i} className="border-l-4 border-[#FF4D00] pl-4">
                <h3 className="display text-xl leading-tight">{h3}</h3>
                <p className="text-sm text-zinc-700 mt-2">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
